/**
 * Builds the modal body content for `GameStateOverlay` when an opponent
 * avatar and/or the `game_over`-only add-opponent control is present. Split
 * out of `GameStateOverlay.ts` to limit how much larger the avatar feature
 * makes that already-oversized file (219 lines pre-existing, itself over the
 * repo's 200-line convention) — this doesn't bring it back under the cap,
 * but keeps the PR's net addition to it small.
 *
 * Caches the `<img>` element across updates so a ticking countdown (body
 * text changes every second on `opponent_disconnected`) doesn't tear down
 * and reassign `.src` on a fresh node every tick — `appendChild` moves the
 * same node into each new wrapper instead of reloading the image.
 */
export interface AvatarBodySpec {
  readonly body?: string;
  readonly avatarUrl?: string | null;
  /** `game_over` only — appended after the body text when present. */
  readonly addOpponentControl?: HTMLElement | null;
  /** `game_over` only — the caller's own node, appended last. */
  readonly extraContent?: HTMLElement | null;
}

export interface AvatarBodyBuilder {
  readonly build: (spec: AvatarBodySpec) => string | HTMLElement;
}

export function createAvatarBodyBuilder(): AvatarBodyBuilder {
  let avatarImg: HTMLImageElement | null = null;
  let avatarImgUrl: string | null = null;

  function getAvatarImg(url: string): HTMLImageElement {
    if (avatarImg === null || avatarImgUrl !== url) {
      const img = document.createElement("img");
      img.className = "modal-avatar";
      // Decorative — the opponent's name is already announced via the modal
      // title text, so an alt description here would be redundant for SR users.
      img.alt = "";
      img.onerror = () => {
        img.style.display = "none";
        // Invalidate the cache entry (not just this node) so a later render
        // with the same URL retries instead of reusing a permanently-hidden
        // node — a transient CDN blip shouldn't hide a valid avatar for the
        // rest of the modal's lifetime.
        if (avatarImgUrl === url) avatarImgUrl = null;
      };
      img.src = url;
      avatarImg = img;
      avatarImgUrl = url;
    }
    return avatarImg;
  }

  function build(spec: AvatarBodySpec): string | HTMLElement {
    if (!spec.avatarUrl && !spec.addOpponentControl && !spec.extraContent) {
      return spec.body ?? "";
    }
    const wrapper = document.createElement("div");
    // Not "modal-body" — this wrapper is appended inside Modal.ts's own
    // .modal-body node, and text-align/color/font-size/line-height are all
    // inherited from that ancestor, so re-adding the class would just
    // produce nested duplicate-class markup for no visual difference.
    wrapper.className = "modal-body-with-avatar";
    if (spec.avatarUrl) {
      wrapper.appendChild(getAvatarImg(spec.avatarUrl));
    }
    if (spec.body) {
      const text = document.createElement("span");
      text.textContent = spec.body;
      wrapper.appendChild(text);
    }
    if (spec.addOpponentControl) {
      wrapper.appendChild(spec.addOpponentControl);
    }
    if (spec.extraContent) {
      // Its own line under the result, however wide the row above is.
      wrapper.classList.add("modal-body-with-avatar--stacked");
      const extra = document.createElement("div");
      extra.className = "modal-extra";
      extra.appendChild(spec.extraContent);
      wrapper.appendChild(extra);
    }
    return wrapper;
  }

  return { build };
}
